/* eslint-disable jsx-a11y/no-onchange */
import useTypedTranslation from '@/hooks/useTypedTranslation';
import event from '@/utilites/gTag';
import { useRouter } from 'next/router';
import { PrimaryButton } from './common/PrimaryButton';
import SvgFacebook from './svg/footer/dark/SvgFacebok';
import SvgInstagram from './svg/footer/dark/SvgInstagram';
import SvgTikTok from './svg/footer/dark/SvgTikTok';
import SvgYoutube from './svg/footer/dark/SvgYoutube';
import SvgPaymentMethods from './svg/footer/SvgPaymentMethods';
import languageConstants from '@/constants/language.constants';
import Image from 'next/image';
import { CommonEnum } from '@/types/translations/CommonEnum';
import Link from 'next/link';
import { FooterEnum } from '@/types/translations/FooterEnum';
import DefaultButton from './common/default/DefaultButton';
import ContactFormV2 from './common/ContactForm/ContactFormV2';
import { useState } from 'react';
import { CgChevronDown } from 'react-icons/cg';
import clsx from 'clsx';
import SvgSpiritoryApp from './svg/footer/SvgSpiritoryApp';
import SvgAppleDark from './svg/footer/SvgAppleDark';
import SvgGoogleDark from './svg/footer/SvgGoogleDark';
import doSubscribeNewsletter from '@/api/doSubscribeNewsletter';
import { toast } from 'react-toastify';
import handleLanguageChange from '@/utilites/handle-language-change';
import { useTranslation } from 'next-i18next';
import { useQueryClient } from '@tanstack/react-query';
import uiItems from '@/constants/ui-items/index.constants';
import { handleGooglePlayLink, handleAppleStoreLink } from '@/utilites/events.utils';
import { useFetchFooterQrCode } from '@/useQuery/queries/qrCode';
import LoadingComponent from './common/LoadingComponent';
import useBackendUser from '@/hooks/useBackendUser';
import Container from './Container';
import {
  ImagesFooter2025FacebookSvg,
  ImagesFooter2025InstagramSvg,
  ImagesFooter2025TiktokSvg,
  ImagesFooter2025YoutubeSvg,
} from '../assets/images_footer-2025.assets';
import DefaultLink from './common/default/DefaultLink';
import { useAnalytics } from '@/hooks/useAnalytics';
import useEvents from '@/hooks/useEvents';

const Footer = () => {
  const { data: user } = useBackendUser();
  const qrCodeFooterQuery = useFetchFooterQrCode();
  const queryClient = useQueryClient();
  const router = useRouter();
  const { locale, push } = router;

  const { translate: translateCommon } = useTypedTranslation<CommonEnum>('common');
  const { translate } = useTypedTranslation<FooterEnum>('footer');
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [email, setEmail] = useState('');
  const { t: tCommon } = useTranslation('common');

  const { getPageType } = useAnalytics();
  const {
    fireGlobalBrandButtonClickEvent,
    fireGlobalCountryButtonClickEvent,
    fireNewsletterSignupButtonClickEvent,
    fireInstagramButtonClickEvent,
    fireTiktokButtonClickEvent,
    fireYoutubeButtonClickEvent,
    fireFacebookButtonClickEvent,
  } = useEvents('', []);

  const handleSocialClick = (
    href: string,
    social: 'facebook' | 'tiktok' | 'youtube' | 'instagram'
  ) => {
    if (social === 'facebook') {
      fireFacebookButtonClickEvent({
        page_type: getPageType(),
      });
    } else if (social === 'tiktok') {
      fireTiktokButtonClickEvent({
        page_type: getPageType(),
      });
    } else if (social === 'youtube') {
      fireYoutubeButtonClickEvent({
        page_type: getPageType(),
      });
    } else if (social === 'instagram') {
      fireInstagramButtonClickEvent({
        page_type: getPageType(),
      });
    }
    window.open(href, '_blank');
  };

  return (
    <footer className="tw-bg-text">
      <Container className="max-nine:tw-px-0">
        <div className="tw-pt-11 max-nine:tw-px-4 nine:tw-flex nine:tw-w-full nine:tw-items-center nine:tw-justify-between">
          <div className="nine:tw-text-left">
            <div className="tw-font-prata tw-text-4xl tw-font-normal tw-text-white max-nine:tw-hidden">
              {translate(FooterEnum['footer.do_you_want_to_be_the_first_to_receive_the_news'])}
            </div>
            <div className="tw-font-prata tw-font-normal tw-text-white max-nine:tw-text-center max-nine:tw-text-xl nine:tw-hidden">
              {translate(FooterEnum['footer.spiritory_newsletter'])}
            </div>
            <p className="tw-m-0 tw-mt-4 tw-p-0 tw-font-medium tw-leading-[130%] tw-text-white max-nine:tw-text-center max-nine:tw-text-base">
              {translate(FooterEnum['footer.sign_up_for_our_newsletter_leave_your_email'])}
            </p>
          </div>
          <form
            className="tw-flex tw-flex-col tw-pt-8 max-nine:tw-gap-6 nine:tw-h-20 nine:tw-flex-row nine:tw-items-stretch"
            onSubmit={async e => {
              e.preventDefault();
              const toastId = toast.loading(
                translate(FooterEnum['footer.loading_subscribing_to_newsletter'])
              );
              try {
                fireNewsletterSignupButtonClickEvent({
                  page_type: getPageType(),
                });
                await doSubscribeNewsletter(email);
                toast.dismiss(toastId);
                toast.success(translate(FooterEnum['footer.succesfully_subscribed']));
              } catch (err) {
                console.log('Error subscribing to newsletter', err);
                toast.dismiss(toastId);
                toast.success(translate(FooterEnum['footer.error_subscribing_to_the_newsletter']));
              }
            }}
          >
            <input
              id="email"
              type="email"
              name="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder={'Email'}
              className={clsx(
                `tw-w-full tw-flex-grow tw-rounded-lg tw-border tw-border-solid tw-border-white/20 tw-px-4 tw-py-3 tw-font-inter tw-text-base tw-font-normal tw-leading-[22px] placeholder:tw-font-inter placeholder:tw-text-base placeholder:tw-font-normal placeholder:tw-leading-[22px] max-nine:tw-min-w-72 max-nine:tw-bg-white/20 max-nine:tw-text-white placeholder:max-nine:tw-text-white nine:tw-rounded-br-none nine:tw-rounded-tr-none nine:tw-bg-white nine:tw-text-text placeholder:nine:tw-text-text`
              )}
            />
            <div className="tw-flex tw-h-full tw-w-full tw-items-center max-nine:tw-hidden max-nine:tw-justify-center">
              <button
                className="tw-h-full tw-rounded-lg tw-rounded-bl-none tw-rounded-tl-none tw-border tw-border-solid tw-border-main tw-bg-main tw-px-4 tw-text-base tw-text-white"
                type="submit"
              >
                {translate(FooterEnum['footer.subscribe'])}
              </button>
            </div>
            <div className="tw-flex tw-w-full tw-items-center max-nine:tw-justify-center nine:tw-hidden">
              <PrimaryButton white={true} className="!tw-py-3 !tw-text-sm" type="submit">
                {translate(FooterEnum['footer.subscribe'])}
              </PrimaryButton>
            </div>
          </form>
        </div>
        <div className="tw-flex tw-items-center tw-justify-center tw-gap-6 tw-px-[77.5px] tw-pb-8 tw-pt-12 nine:tw-hidden">
          <DefaultButton
            onClick={() => {
              handleSocialClick('https://web.facebook.com/Spiritory', 'facebook');
            }}
          >
            <SvgFacebook />
          </DefaultButton>
          <DefaultButton
            onClick={() => {
              handleSocialClick('https://www.instagram.com/spiritory_official/', 'instagram');
            }}
          >
            <SvgInstagram />
          </DefaultButton>
          <DefaultButton
            onClick={() => {
              handleSocialClick('https://www.tiktok.com/@spiritory', 'tiktok');
            }}
          >
            <SvgTikTok />
          </DefaultButton>
          <DefaultButton
            onClick={() => {
              handleSocialClick('https://www.youtube.com/@spiritory', 'youtube');
            }}
          >
            <SvgYoutube />
          </DefaultButton>
        </div>
        <hr className="tw-m-0 tw-mx-auto tw-my-12 tw-h-px tw-w-full tw-border-none tw-bg-white/40 tw-opacity-100 max-nine:tw-hidden" />
        <hr className="tw-m-0 tw-mx-auto tw-h-px tw-max-w-60 tw-border-none tw-bg-custom-gold tw-px-16 tw-opacity-100 nine:tw-hidden" />
        <div className="tw-grid tw-grid-cols-4 tw-gap-x-10 tw-gap-y-8 tw-px-4 tw-pb-16 tw-pt-8 max-nine:tw-grid-cols-1 nine:tw-px-0">
          {uiItems
            .footer(
              getPageType,
              fireGlobalBrandButtonClickEvent as any,
              fireGlobalCountryButtonClickEvent as any,
              router.push
            )
            .footerItems.map(item => (
              <ul
                className="tw-m-0 tw-flex tw-flex-col tw-gap-3 tw-p-0 child:tw-list-none"
                key={item.title}
              >
                <li className="tw-mb-3">
                  <h2 className="tw-text-base tw-font-[800] tw-leading-[140%] tw-text-white">
                    {translate(item.title as FooterEnum)}
                  </h2>
                </li>
                {item.items.map(subItem =>
                  subItem.href ? (
                    <li
                      className="tw-text-sm tw-font-medium tw-leading-[140%] tw-text-white"
                      key={JSON.stringify(subItem)}
                    >
                      <Link href={subItem.href} className="tw-text-white tw-no-underline">
                        {translate(subItem.label as FooterEnum)}
                      </Link>
                    </li>
                  ) : subItem.onClick ? (
                    <li
                      className="tw-text-sm tw-font-medium tw-leading-[140%] tw-text-white"
                      key={JSON.stringify(subItem)}
                    >
                      <DefaultButton
                        onClick={subItem.onClick}
                        className="tw-cursor-pointer tw-border-none tw-bg-transparent tw-p-0 tw-text-white tw-no-underline"
                      >
                        {translate(subItem.label as FooterEnum)}
                      </DefaultButton>
                    </li>
                  ) : null
                )}
              </ul>
            ))}
        </div>
        <hr className="tw-m-0 tw-my-12 tw-h-px tw-w-full tw-border-none tw-bg-white/40 tw-opacity-100 max-nine:tw-hidden" />

        <hr className="tw-m-0 tw-mx-auto tw-h-px tw-max-w-60 tw-border-none tw-bg-custom-gold tw-px-16 tw-opacity-100 nine:tw-hidden" />
        <div className="tw-pb-16 max-nine:tw-px-4 max-nine:tw-pt-10 nine:tw-flex nine:tw-items-end nine:tw-justify-between">
          <div className="max-nine:tw-hidden">
            <div className="tw-flex tw-flex-col">
              <p className="tw-m-0 tw-mb-4 tw-p-0">
                <strong className="tw-text-xl tw-font-semibold tw-leading-[140%] tw-text-white">
                  {translate(FooterEnum['footer.spiritory_apps'])}
                </strong>
              </p>
              <div className="tw-flex tw-items-stretch tw-gap-4">
                <div>
                  {qrCodeFooterQuery.isLoading ? (
                    <LoadingComponent loading={true} as="div">
                      <span className="tw-h-[150px] tw-w-[150px]">...</span>
                    </LoadingComponent>
                  ) : qrCodeFooterQuery.data ? (
                    <Image
                      src={`data:image/png;base64,${qrCodeFooterQuery.data}`}
                      alt="Spiritory QR-CODE"
                      width={150}
                      height={150}
                    />
                  ) : (
                    <SvgSpiritoryApp width={150} height={150} />
                  )}
                </div>
                <div className="tw-flex tw-flex-col tw-items-stretch tw-justify-between tw-gap-2 child:tw-flex child:tw-items-center child:tw-gap-6 child:tw-rounded-xl child:tw-bg-white child:tw-px-6 child:tw-py-3">
                  <DefaultButton
                    className="tw-text-base tw-font-medium tw-text-textBlack"
                    onClick={() => handleAppleStoreLink(user?.id || '', locale)}
                  >
                    <SvgAppleDark />
                    <span>
                      {translate(FooterEnum['footer.download_on'])} <br />
                      <strong className="tw-text-base tw-font-bold tw-text-textBlack">
                        Apple Store
                      </strong>
                    </span>
                  </DefaultButton>
                  <DefaultButton
                    className="tw-text-base tw-font-medium tw-text-textBlack"
                    onClick={() => handleGooglePlayLink(user?.id || '', locale)}
                  >
                    <SvgGoogleDark />
                    <span>
                      {translate(FooterEnum['footer.download_on'])}
                      <br />
                      <strong className="tw-text-base tw-font-bold tw-text-textBlack">
                        Google Play
                      </strong>
                    </span>
                  </DefaultButton>
                </div>
              </div>
            </div>
          </div>
          <div>
            <ul className="tw-m-0 tw-flex tw-gap-6 tw-p-0 child:tw-list-none">
              <li>
                <DefaultButton
                  onClick={() => {
                    handleSocialClick('https://web.facebook.com/Spiritory', 'facebook');
                  }}
                >
                  <Image src={ImagesFooter2025FacebookSvg} alt="Facebook" width={37} height={37} />
                </DefaultButton>
              </li>
              <li>
                <DefaultButton
                  onClick={() => {
                    handleSocialClick('https://www.instagram.com/spiritory_official/', 'instagram');
                  }}
                >
                  <Image
                    src={ImagesFooter2025InstagramSvg}
                    alt="Instagram"
                    width={37}
                    height={37}
                  />
                </DefaultButton>
              </li>
              <li>
                <DefaultButton
                  onClick={() => {
                    handleSocialClick('https://www.tiktok.com/@spiritory', 'tiktok');
                  }}
                >
                  <Image
                    src={ImagesFooter2025TiktokSvg}
                    alt="Tiktok"
                    width={37}
                    height={37}
                    className="tw-rounded-md tw-bg-[#2d3a52] tw-p-0.5"
                  />
                </DefaultButton>
              </li>
              <li>
                <DefaultButton
                  onClick={() => {
                    handleSocialClick('https://www.youtube.com/@spiritory', 'youtube');
                  }}
                >
                  <Image src={ImagesFooter2025YoutubeSvg} alt="Youtube" width={37} height={37} />
                </DefaultButton>
              </li>
            </ul>
          </div>
          <ul className="tw-m-0 tw-flex tw-flex-col tw-gap-6 tw-p-0 child:tw-list-none">
            <li className="tw-flex tw-flex-col tw-gap-4">
              <h2 className="tw-mb-0 tw-text-base tw-font-semibold tw-leading-[140%] tw-text-white">
                {translate(FooterEnum['footer.payment_methods'])}
              </h2>
              <SvgPaymentMethods width={311} height={32} />
            </li>
            <li className="tw-flex tw-flex-col tw-gap-4">
              <h2 className="tw-mb-0 tw-text-base tw-font-semibold tw-leading-[140%] tw-text-white">
                {translate(FooterEnum['footer.language'])}{' '}
              </h2>
              <div className="tw-relative">
                <select
                  className="tw-w-full tw-appearance-none tw-rounded-xl tw-border-none tw-bg-white/10 tw-py-4 tw-pl-12 tw-pr-4 tw-text-white"
                  value={locale}
                  onChange={async e => {
                    try {
                      await handleLanguageChange(
                        e.target.value,
                        tCommon,
                        router,
                        Boolean(user),
                        queryClient
                      );
                    } catch (error) {
                      console.error('Error changing language:', error);
                    }
                  }}
                >
                  {Object.values(languageConstants.localesWithIcons).map(locale => (
                    <option key={locale.locale} value={locale.locale} className="tw-text-text">
                      {translateCommon(CommonEnum[('languages.' + locale.locale) as CommonEnum])}
                    </option>
                  ))}
                </select>
                <Image
                  src={
                    languageConstants.localesWithIcons[
                      `${(locale as keyof typeof languageConstants.localesWithIcons) || 'en'}`
                    ].icon
                  }
                  alt={
                    languageConstants.localesWithIcons[
                      `${(locale as keyof typeof languageConstants.localesWithIcons) || 'en'}`
                    ].locale
                  }
                  width={24}
                  height={24}
                  className="tw-absolute tw-left-4 tw-top-1/2 tw--translate-y-1/2 tw-transform"
                />
                <CgChevronDown className="tw-absolute tw-right-4 tw-top-1/2 tw--translate-y-1/2 tw-transform tw-text-white" />
              </div>
            </li>
          </ul>
        </div>
        <div className="tw-flex tw-flex-col tw-gap-10 tw-pb-14 max-nine:tw-hidden">
          <div className="tw-flex tw-items-center tw-justify-between child:tw-text-white child:tw-no-underline">
            <p className="tw-font-base tw-m-auto tw-font-medium tw-leading-[140%] tw-text-white">
              &copy; {new Date().getFullYear()} Spiritory.{' '}
              {translate(FooterEnum['footer.all_rights_reserved'])}{' '}
            </p>
          </div>
        </div>

        <div className="tw-flex tw-flex-col tw-gap-10 tw-px-14 tw-pb-14 nine:tw-hidden">
          <p className="tw-font-base tw-m-0 tw-font-medium tw-leading-[140%] tw-text-white">
            &copy; {new Date().getFullYear()} Spiritory.{' '}
            {translate(FooterEnum['footer.all_rights_reserved'])}{' '}
          </p>
        </div>
        <ContactFormV2 isOpen={contactModalOpen} onClose={() => setContactModalOpen(false)} />
      </Container>
    </footer>
  );
};

export default Footer;
